// Lightweight localStorage snapshot store for in-progress practice/exam
// sessions, so a refresh or tab close doesn't lose the user's answers.
// One key per flow; snapshots carry a version and a 7-day TTL.

const VERSION = 1;
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DEBOUNCE_MS = 500;

const pendingWrites = new Map(); // storageKey -> { timer, data }

function persist(key, data) {
  try {
    window.localStorage.setItem(key, JSON.stringify({ v: VERSION, savedAt: Date.now(), data }));
  } catch {
    // Quota exceeded or storage unavailable, degrade silently.
  }
}

// A reload or tab close inside the debounce window would drop the last change
// (the final keystrokes of a writing task, the last second of audio progress).
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    pendingWrites.forEach(({ timer, data }, key) => {
      clearTimeout(timer);
      persist(key, data);
    });
    pendingWrites.clear();
  });
}

export function readSession(key) {
  if (!key || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== VERSION) return null;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed.data ?? null;
  } catch {
    return null;
  }
}

export function writeSession(key, data) {
  if (!key || typeof window === 'undefined') return;
  const existing = pendingWrites.get(key);
  if (existing) clearTimeout(existing.timer);
  const timer = setTimeout(() => {
    pendingWrites.delete(key);
    persist(key, data);
  }, DEBOUNCE_MS);
  pendingWrites.set(key, { timer, data });
}

export function clearSession(key) {
  if (!key || typeof window === 'undefined') return;
  const existing = pendingWrites.get(key);
  if (existing) {
    clearTimeout(existing.timer);
    pendingWrites.delete(key);
  }
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

// ─── Exam simulator snapshot keys ────────────────────────────────────────────

/**
 * The simulator's in-progress snapshot key for a loaded paper. One builder, so
 * the guest ('anon') key and the signed-in key can never drift apart.
 * @param {string|null} uid
 * @param {{ examProvider: string, level: string, examFileName?: string, variantName?: string }} examData
 */
export function examSessionKey(uid, examData) {
  return `logami:v1:exam:${uid || 'anon'}:${examData.examProvider}-${examData.level}:${examData.examFileName || 'default'}:${examData.variantName || 'original'}`;
}

/**
 * Hand a guest's anonymous snapshot to the account they just signed into.
 *
 * The guest flow (/try) and the in-app loader serve the SAME free paper but
 * label it differently (`loadGuestExam` uses the publishable-paper label,
 * `loadExam` the catalog's variant name), so the key's trailing variant segment
 * differs. Match on the provider-level prefix instead and prefer the exact
 * file, so the answers follow the user into whichever entry point they resume
 * from. The snapshot payload is answer/index data against the same paper, so
 * it stays valid across the two labels.
 *
 * Never clobbers an existing session for the uid. Raw localStorage, not
 * writeSession: the debounce could lose the copy before the simulator mounts.
 *
 * @param {string} uid
 * @param {{ examProvider: string, level: string, examFileName?: string, variantName?: string }} examData
 * @returns {boolean} true if a snapshot was adopted
 */
export function adoptAnonExamSession(uid, examData) {
  if (!uid || !examData || typeof window === 'undefined') return false;
  try {
    const uidKey = examSessionKey(uid, examData);
    if (window.localStorage.getItem(uidKey)) return true; // already has a session

    const prefix = `logami:v1:exam:anon:${examData.examProvider}-${examData.level}:`;
    const exact = `${prefix}${examData.examFileName || 'default'}:`;
    const candidates = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(prefix)) candidates.push(key);
    }
    const anonKey = candidates.find((k) => k.startsWith(exact)) || candidates[0];
    if (!anonKey) return false;

    const raw = window.localStorage.getItem(anonKey);
    if (raw) window.localStorage.setItem(uidKey, raw);
    window.localStorage.removeItem(anonKey);
    return Boolean(raw);
  } catch {
    // Storage unavailable, the user restarts the exam, nothing broken.
    return false;
  }
}

/**
 * Drop every anonymous exam snapshot on this device. Called on logout: whatever
 * a guest left under the 'anon' key belongs to the session that is ending, and
 * the next account signed in here must not be able to adopt its answers. The
 * paper exam's guest sittings (useSittingState) likewise.
 */
export function clearAnonExamSessions() {
  if (typeof window === 'undefined') return;
  try {
    const prefixes = ['logami:v1:exam:anon:', 'paperExam:anon:'];
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && prefixes.some((prefix) => key.startsWith(prefix))) keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Storage unavailable, nothing to clear.
  }
}
